import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Loader2, GripVertical, Type, Hash, Calendar, Upload, Download, FileText, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ExportImportButtons } from '../components/ExportImportButtons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMN_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
];

export default function DatasheetDetailPage() {
  const { datasheetId } = useParams();
  const navigate = useNavigate();
  const [datasheet, setDatasheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAddColOpen, setIsAddColOpen] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColType, setNewColType] = useState('text');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);
  const fileInputRef = useRef(null);
  const [csvExporting, setCsvExporting] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);

  const handleExportDatasheetCSV = async () => {
    setCsvExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/datasheets/${datasheetId}/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${datasheet?.name || 'datasheet'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setCsvExporting(false);
    }
  };

  const handleImportDatasheetCSV = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/datasheets/${datasheetId}/import/csv`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} row(s)`);
        fetchDatasheet();
      } else {
        toast.info('No rows imported');
      }
    } catch {
      toast.error('Failed to import CSV');
    } finally {
      setCsvImporting(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    fetchDatasheet();
  }, [datasheetId]);

  const fetchDatasheet = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/datasheets/${datasheetId}`, { withCredentials: true });
      setDatasheet(res.data);
      setRows(res.data.rows || []);
    } catch {
      toast.error('Failed to load datasheet');
      navigate('/datasheets');
    } finally {
      setLoading(false);
    }
  };

  const addColumn = async () => {
    if (!newColName.trim()) { toast.error('Column name is required'); return; }
    const newCol = { id: crypto.randomUUID(), name: newColName.trim(), type: newColType };
    const updatedColumns = [...(datasheet.columns || []), newCol];
    try {
      await axios.put(`${API_URL}/api/datasheets/${datasheetId}`, { columns: updatedColumns }, { withCredentials: true });
      setDatasheet({ ...datasheet, columns: updatedColumns });
      setIsAddColOpen(false);
      setNewColName('');
      setNewColType('text');
      toast.success('Column added');
    } catch {
      toast.error('Failed to add column');
    }
  };

  const removeColumn = async (colId) => {
    const updatedColumns = datasheet.columns.filter(c => c.id !== colId);
    try {
      await axios.put(`${API_URL}/api/datasheets/${datasheetId}`, { columns: updatedColumns }, { withCredentials: true });
      setDatasheet({ ...datasheet, columns: updatedColumns });
      toast.success('Column removed');
    } catch {
      toast.error('Failed to remove column');
    }
  };

  const addRow = async () => {
    const emptyData = {};
    (datasheet.columns || []).forEach(col => { emptyData[col.id] = ''; });
    try {
      const res = await axios.post(`${API_URL}/api/datasheets/${datasheetId}/rows`, { data: emptyData }, { withCredentials: true });
      setRows([...rows, res.data]);
    } catch {
      toast.error('Failed to add row');
    }
  };

  const deleteRow = async (rowId) => {
    try {
      await axios.delete(`${API_URL}/api/datasheets/${datasheetId}/rows/${rowId}`, { withCredentials: true });
      setRows(rows.filter(r => r.id !== rowId));
    } catch {
      toast.error('Failed to delete row');
    }
  };

  const startEdit = (rowId, colId, currentValue) => {
    setEditingCell({ rowId, colId });
    setEditValue(currentValue || '');
    setTimeout(() => editRef.current?.focus(), 0);
  };

  const commitEdit = useCallback(async () => {
    if (!editingCell) return;
    const { rowId, colId } = editingCell;
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    const newData = { ...row.data, [colId]: editValue };
    setRows(rows.map(r => r.id === rowId ? { ...r, data: newData } : r));
    setEditingCell(null);

    try {
      await axios.put(`${API_URL}/api/datasheets/${datasheetId}/rows/${rowId}`, { data: newData }, { withCredentials: true });
    } catch {
      toast.error('Failed to save cell');
      fetchDatasheet();
    }
  }, [editingCell, editValue, rows, datasheetId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditingCell(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (10MB max)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/files/upload?company_id=${datasheet.company_id}&datasheet_id=${datasheetId}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded');
      fetchDatasheet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteFile = async () => {
    if (!deleteFileTarget) return;
    try {
      await axios.delete(`${API_URL}/api/files/${deleteFileTarget.id}`, { withCredentials: true });
      toast.success('File deleted');
      setDeleteFileTarget(null);
      fetchDatasheet();
    } catch { toast.error('Failed to delete file'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  if (!datasheet) return null;

  const columns = datasheet.columns || [];

  return (
    <div className="space-y-4" data-testid="datasheet-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/datasheets')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-testid="back-to-datasheets">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">{datasheet.name}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportDatasheetCSV} onImportCSV={handleImportDatasheetCSV} exporting={csvExporting} importing={csvImporting} testIdPrefix="datasheet" />
          <Button variant="outline" size="sm" onClick={() => { setNewColName(''); setNewColType('text'); setIsAddColOpen(true); }} data-testid="add-column-btn">
            <Plus className="w-4 h-4 mr-1" />Column
          </Button>
          <Button size="sm" onClick={addRow} data-testid="add-row-btn">
            <Plus className="w-4 h-4 mr-1" />Row
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-2 py-2 w-10 text-center text-xs font-medium text-gray-400">#</th>
              {columns.map((col) => (
                <th key={col.id} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase group">
                  <div className="flex items-center justify-between">
                    <span>{col.name}</span>
                    <button
                      onClick={() => removeColumn(col.id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity ml-2"
                      data-testid={`remove-col-${col.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </th>
              ))}
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="text-center py-12 text-gray-400 text-sm">
                  No rows yet. Click "+ Row" to add data.
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`datasheet-row-${row.id}`}>
                  <td className="px-2 py-1 text-center text-xs text-gray-400">{idx + 1}</td>
                  {columns.map((col) => {
                    const isEditing = editingCell?.rowId === row.id && editingCell?.colId === col.id;
                    return (
                      <td
                        key={col.id}
                        className="px-1 py-1 border-r border-gray-100 last:border-r-0"
                        onClick={() => !isEditing && startEdit(row.id, col.id, row.data?.[col.id])}
                        data-testid={`cell-${row.id}-${col.id}`}
                      >
                        {isEditing ? (
                          <input
                            ref={editRef}
                            type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={commitEdit}
                            onKeyDown={handleKeyDown}
                            className="w-full px-2 py-1 text-sm border border-blue-400 rounded outline-none bg-blue-50/50"
                            data-testid={`cell-input-${row.id}-${col.id}`}
                          />
                        ) : (
                          <div className="px-2 py-1 text-sm text-gray-700 min-h-[28px] cursor-text hover:bg-gray-100 rounded">
                            {row.data?.[col.id] || <span className="text-gray-300">-</span>}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-1 py-1">
                    <button onClick={() => deleteRow(row.id)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-row-${row.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Files Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-medium text-gray-900 flex items-center gap-2"><FolderOpen className="w-4 h-4" />Files ({datasheet.files?.length || 0})</h2>
          <div>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="datasheet-file-input" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="datasheet-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </div>
        </div>
        <div className="p-4">
          {datasheet.files?.length > 0 ? (
            <div className="space-y-2">
              {datasheet.files.map((file) => (
                <div key={file.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg group" data-testid={`datasheet-file-${file.id}`}>
                  <FileText className="w-4 h-4 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.filename}</p>
                    <p className="text-xs text-gray-500">{new Date(file.created_at).toLocaleDateString()} · {formatFileSize(file.size)}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                    <a href={`${API_URL}/api/files/${file.id}/download`} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="h-7 w-7"><Download className="w-3.5 h-3.5" /></Button>
                    </a>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setDeleteFileTarget(file)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <FolderOpen className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">No files attached</p>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4 mr-1" />Upload File</Button>
            </div>
          )}
        </div>
      </div>

      {/* Add Column Dialog */}
      <Dialog open={isAddColOpen} onOpenChange={setIsAddColOpen}>
        <DialogContent data-testid="add-column-dialog">
          <DialogHeader><DialogTitle>Add Column</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Column Name *</Label>
              <Input value={newColName} onChange={(e) => setNewColName(e.target.value)} placeholder="e.g. Amount" data-testid="column-name-input" onKeyDown={(e) => e.key === 'Enter' && addColumn()} />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newColType} onValueChange={setNewColType}>
                <SelectTrigger data-testid="column-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLUMN_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddColOpen(false)}>Cancel</Button>
            <Button onClick={addColumn} data-testid="save-column-btn">Add Column</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete File Dialog */}
      <AlertDialog open={!!deleteFileTarget} onOpenChange={(o) => !o && setDeleteFileTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete File</AlertDialogTitle><AlertDialogDescription>Delete "{deleteFileTarget?.filename}"?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
